/**
 * Converts a hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB values to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Calculates the relative luminance of a color (for contrast calculation)
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determines if white or black text provides better contrast
 */
function getContrastText(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#fff';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  // If luminance is less than 0.5, use white text, otherwise use black
  return luminance < 0.5 ? '#fff' : '#000';
}

/**
 * Lightens a color by a percentage
 */
function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor));

  return rgbToHex(r, g, b);
}

/**
 * Darkens a color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  const r = Math.max(0, Math.round(rgb.r * (1 - factor)));
  const g = Math.max(0, Math.round(rgb.g * (1 - factor)));
  const b = Math.max(0, Math.round(rgb.b * (1 - factor)));

  return rgbToHex(r, g, b);
}

/**
 * Generates a color palette with shades and tints from a base color
 * Based on the pattern used in the theme: darker, dark, main, light, lighter
 */
export function generateColorPalette(baseColor: string): {
  darker: string;
  dark: string;
  main: string;
  light: string;
  lighter: string;
  contrastText: string;
} {
  // Normalize hex color (ensure it starts with #)
  const normalizedColor = baseColor.startsWith('#') ? baseColor : `#${baseColor}`;

  // Validate hex color format
  if (!/^#[0-9A-Fa-f]{6}$/.test(normalizedColor)) {
    console.warn(`Invalid color format: ${baseColor}. Using default color.`);
    return {
      darker: '#802418',
      dark: '#cc3a26',
      main: '#ff4830',
      light: '#ff6d59',
      lighter: '#ffa498',
      contrastText: '#fff',
    };
  }

  return {
    darker: darkenColor(normalizedColor, 50), // 50% darker
    dark: darkenColor(normalizedColor, 20), // 20% darker
    main: normalizedColor,
    light: lightenColor(normalizedColor, 20), // 20% lighter
    lighter: lightenColor(normalizedColor, 50), // 50% lighter
    contrastText: getContrastText(normalizedColor),
  };
}

